import { formatWithComma } from "./formatWithComma";

/**
 * Nepali rupee amounts for screens that show money to a cinema company
 * (Hall-E usage, limits and statements; claude/design/hall-e/01 R18).
 *
 * The API sends NPR as decimal strings ("493.36"). They are parsed to paisa
 * as a BigInt, so nothing is ever computed in floating point, and the
 * rupees are grouped the Nepali way (1,25,000) by the shared
 * `formatWithComma`. Nothing here converts a currency: an amount that is not
 * already in rupees has no business on these screens.
 *
 * Whole rupees from रु 100 up, rupees and paisa below (Q30): a tiny amount
 * reads "रु 1.38 of रु 1.50", never "रु 1 of रु 2".
 */

/** The rupee sign printed before an amount. */
export const NPR_SYMBOL = "रु";

/** What a missing or unreadable amount shows as. */
export const NPR_EMPTY = "—";

/** Below this many paisa (रु 100) an amount keeps its paisa (Q30). */
export const NPR_PAISA_BELOW = 10000;

const AMOUNT = /^\s*(-)?(\d*)(?:\.(\d*))?\s*$/;
// BigInt calls, not `100n` literals: the apps compile to ES2016.
const ZERO = BigInt(0);
const ONE = BigInt(1);
const TWO = BigInt(2);
const FIFTY = BigInt(50);
const HUNDRED = BigInt(100);

/**
 * An NPR amount in paisa, or `null` when `value` is not an amount. Digits
 * past the paisa are rounded half up (away from zero).
 */
export function nprToPaisa(
  value: string | number | null | undefined,
): bigint | null {
  if (value === null || value === undefined) return null;
  let text: string;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    text = value.toFixed(4);
  } else {
    text = value;
  }
  const match = AMOUNT.exec(text);
  if (!match) return null;
  const [, sign, whole = "", fraction = ""] = match;
  if (!whole && !fraction) return null;
  const digits = fraction.padEnd(3, "0");
  let paisa = BigInt(whole || "0") * HUNDRED + BigInt(digits.slice(0, 2));
  if (Number(digits[2]) >= 5) paisa += ONE;
  return sign && paisa !== ZERO ? -paisa : paisa;
}

/** Whole rupees grouped the Nepali way: 125000 → "1,25,000". */
function groupRupees(rupees: bigint): string {
  return formatWithComma(rupees.toString(), 0, "nepali");
}

export interface FormatNprOptions {
  /**
   * `"auto"` (the default): rupees and paisa below रु 100, whole rupees from
   * रु 100 up. 0: always whole rupees, rounded half up. 2: always paisa.
   */
  decimals?: 0 | 2 | "auto";
  /** Print `रु ` before the figure (default true). */
  symbol?: boolean;
}

/** Format an amount already in paisa. */
export function formatPaisa(
  paisa: bigint,
  { decimals = "auto", symbol = true }: FormatNprOptions = {},
): string {
  const negative = paisa < ZERO;
  const abs = negative ? -paisa : paisa;
  const places =
    decimals === "auto" ? (abs < BigInt(NPR_PAISA_BELOW) ? 2 : 0) : decimals;
  let figure: string;
  if (places === 2) {
    const rest = (abs % HUNDRED).toString().padStart(2, "0");
    figure = `${groupRupees(abs / HUNDRED)}.${rest}`;
  } else {
    figure = groupRupees((abs + FIFTY) / HUNDRED);
  }
  const shown = symbol ? `${NPR_SYMBOL} ${figure}` : figure;
  return negative && figure.replace(/[0.,]/g, "") !== "" ? `-${shown}` : shown;
}

/**
 * "रु 1,25,000" from the API's `"125000.00"`, "रु 1.38" from `"1.38"`: whole
 * rupees from रु 100 up and paisa below by default; `{decimals: 0 | 2}`
 * forces one. Unreadable input shows as "—".
 */
export function formatNpr(
  value: string | number | null | undefined,
  options: FormatNprOptions = {},
): string {
  const paisa = nprToPaisa(value);
  return paisa === null ? NPR_EMPTY : formatPaisa(paisa, options);
}

/** Paisa in one lakh (रु 1,00,000) and one crore (रु 1,00,00,000). */
const LAKH_PAISA = BigInt(10000000);
const CRORE_PAISA = BigInt(1000000000);

/** `abs` paisa in hundredths of `unit`, rounded half up. */
function hundredthsOf(abs: bigint, unit: bigint): bigint {
  const step = unit / HUNDRED;
  return (abs * TWO + step) / (step * TWO);
}

/** "1.86", "2.4", "12": a count of hundredths with trailing zeros dropped. */
function trimmedHundredths(hundredths: bigint): string {
  const whole = formatWithComma((hundredths / HUNDRED).toString(), 0, "nepali");
  const rest = (hundredths % HUNDRED)
    .toString()
    .padStart(2, "0")
    .replace(/0+$/, "");
  return rest ? `${whole}.${rest}` : whole;
}

/**
 * The short form for a tight place (a ring's centre, a chart's axis):
 * "रु 1.86 L" from 1,86,420, "रु 2.4 Cr" from 2,40,00,000, at most two
 * decimals of the lakh or crore, rounded half up in paisa. Below a lakh it
 * is `formatNpr` as it is ("रु 74,940"). Unreadable input shows as "—".
 */
export function formatNprCompact(
  value: string | number | null | undefined,
  { symbol = true }: { symbol?: boolean } = {},
): string {
  const paisa = nprToPaisa(value);
  if (paisa === null) return NPR_EMPTY;
  const negative = paisa < ZERO;
  const abs = negative ? -paisa : paisa;
  const crores = hundredthsOf(abs, CRORE_PAISA);
  const lakhs = hundredthsOf(abs, LAKH_PAISA);
  let figure: string;
  if (crores >= HUNDRED) figure = `${trimmedHundredths(crores)} Cr`;
  else if (lakhs >= HUNDRED) figure = `${trimmedHundredths(lakhs)} L`;
  else return formatPaisa(paisa, { symbol });
  const shown = symbol ? `${NPR_SYMBOL} ${figure}` : figure;
  return negative ? `-${shown}` : shown;
}

/**
 * A change in percent as the dashboards write it (amphitheater's words):
 * "▲ 12.4%", "▼ 4.2%", "▲ 38%" (one decimal, a trailing ".0" dropped, the
 * arrow carrying the sign), "No change" when it rounds to nothing. `null`
 * or a value that is not a number: "".
 */
export function formatPercentChange(change: number | null | undefined): string {
  return formatChange(change, "%");
}

/**
 * A change of a percentage in points: "▲ 3.1 pts", "▼ 0.4 pts", "No change".
 * A share is compared in points, never in percent of a percent.
 */
export function formatPointsChange(change: number | null | undefined): string {
  return formatChange(change, " pts");
}

function formatChange(change: number | null | undefined, unit: string): string {
  if (change === null || change === undefined || !Number.isFinite(change))
    return "";
  const rounded = Math.round(Math.abs(change) * 10) / 10;
  if (rounded === 0) return "No change";
  const figure = `${formatWithComma(rounded, 1, "nepali")}${unit}`;
  return `${change > 0 ? "▲" : "▼"} ${figure}`;
}

/**
 * The average cost of one question ("रु 1.20"), from a total and a count, by
 * the same rule as `formatNpr`. Integer paisa arithmetic, rounded half up;
 * "—" without questions.
 */
export function formatNprPerItem(
  total: string | number | null | undefined,
  count: number,
): string {
  const paisa = nprToPaisa(total);
  if (paisa === null || !Number.isInteger(count) || count <= 0) {
    return NPR_EMPTY;
  }
  const items = BigInt(count);
  const negative = paisa < ZERO;
  const abs = negative ? -paisa : paisa;
  const each = (abs * TWO + items) / (items * TWO);
  return formatPaisa(negative ? -each : each);
}
